import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CargoTypeInformation from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/CargoTypeInformation';
import {
  ContainerParameters,
  RouteWidgetBaseParameters,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import { ContainersData } from '@services/requests/searchLoaders/containersLoader/ContainersLoaderQuery';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  checkEmptyInputValue,
  getFormattedIntNumber,
  getFormattedNumberForInput,
} from '../../../helpers';
import useDelivery from '../../../useDelivery';
import { CrossDockingPalletsParameters } from '../CrossDockingPalletsParameters';

// Свойства компонента
export interface ContainerParametersItemComponentProps {
  Position: number;
  BaseData: RouteWidgetBaseParameters;
  ContainerParameters: ContainerParameters;
  mainLanguage: string; // Текущий основной язык
  systemLanguage: string; // Системный язык
  isDeletable: boolean;
  onChange: { (params: ContainerParameters, field: keyof ContainerParameters): void };
  onDelete: { (): void };
  onChangeCargoFieldsFilled: { (values: boolean[]): void };
}

const ContainerParametersItemComponent: FC<ContainerParametersItemComponentProps> = ({
  BaseData,
  ContainerParameters,
  onDelete,
  isDeletable,
  onChange,
  Position,
  systemLanguage,
  onChangeCargoFieldsFilled,
}) => {
  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const [routeParams, setRouteParams] = useRouteParams().useState();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();
  const { t } = useTranslation('Common');
  const { containers, units } = BaseData;

  const isQuantityInputInvalid = checkEmptyInputValue(ContainerParameters.containerQuantity);
  const isWeightInputInvalid = checkEmptyInputValue(ContainerParameters.eachContainerWeight);

  const palletUnits = useMemo(() => {
    const foundContainer = containers.find((c) => c.id === ContainerParameters.containerId?.toString());
    const uniquePalletIds = Array.from(
      new Set((foundContainer?.countPallets ?? []).map((p) => p.type_pallet))
    );

    return uniquePalletIds
      .map((palletId) => units.find((unit) => unit.id === palletId))
      .filter(Boolean);
  }, [containers, units, ContainerParameters.containerId]);

  useEffect(() => {
    onChangeCargoFieldsFilled([isWeightInputInvalid, isQuantityInputInvalid]);
  }, [isWeightInputInvalid, isQuantityInputInvalid]);

  const formattedContainers = useMemo(() => {
    const groupedContainers: Record<number, ContainersData[]> = {};
    const uniqueContainers: ContainersData[] = [];
    const uniqueContainerNames: string[] = [];

    containers.forEach((container) => {
      const { containerType } = container;

      if (containerType.id in groupedContainers) {
        groupedContainers[containerType.id].push(container);
        return;
      }

      groupedContainers[containerType.id] = [container];
    });

    const sortedGroups = Object.entries(groupedContainers).sort(([aKey], [bKey]) =>
      aKey > bKey ? 1 : -1
    );

    const fullSortedContainers = sortedGroups.map(([_, groupContainers]) => {
      const sortedCotainers = groupContainers.sort((a) =>
        a.is_default_for_container_type === true ? -1 : 1
      );

      return sortedCotainers;
    });

    const flatContainers = fullSortedContainers.flat();

    flatContainers.forEach((container) => {
      const isExist = uniqueContainerNames.includes(container.default_name);

      if (!isExist || container.is_default_for_container_type) {
        uniqueContainerNames.push(container.default_name);
        uniqueContainers.push(container);
      }
    });

    return uniqueContainers;
  }, [containers]);

  useEffect(() => {
    const { containerId } = ContainerParameters;
    const defaultContainer = formattedContainers[0];

    if (!containerId) {
      setRouteParams((prevState) => ({
        ...prevState,
        containerParameters: {
          ...prevState.containerParameters,
          containerParameters: prevState.containerParameters.containerParameters.map((c) => ({
            ...c,
            containerId: c.containerId ?? defaultContainer.id,
            eachContainerWeight: c.eachContainerWeight ?? defaultContainer.default_weight,
          })),
        },
      }));
    }
  }, [formattedContainers]);

  /**
   * Обработка изменения одного из значений параметров контейнера
   * @param property
   * @param value
   */
  const handleChangeContainerProperty = <T extends keyof ContainerParameters>(
    property: T,
    value: ContainerParameters[T]
  ) => {
    const params = { ...ContainerParameters };
    params[property] = value;

    onChange(params, property);
  };

  /**
   * Получение локализованного названия
   * @param defaultName
   * @param localizations
   */
  const handleGenerateLocalizedName = (
    defaultName: string,
    localizations: LocalizedMessage[]
  ): string => {
    const localization = localizations.find((l) => l.lang_id === systemLanguage);
    if (!localization || localization.message.length === 0) {
      return defaultName;
    }

    return localization.message;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography
        variant={'subtitle2'}
        color={'grey[700]'}
        marginBottom={'15px'}
        sx={{
          mb: {
            sx: 15,
            md: 18,
            lg: 15,
          },
        }}
        textTransform={'capitalize'}
      >
        {t('Common:Container')} #{Position}
      </Typography>
      <Box className={'cargo-inputs-block'}>
        <Box width={'100%'}>
          <TextField
            fullWidth
            variant={'standard'}
            value={ContainerParameters.containerId ?? ''}
            select
            sx={{
              '& .MuiInput-input': {
                color: '#383838!important',
                fontStyle: 'normal!important',
                fontWeight: '600!important',
                lineHeight: '22px!important',
                textAlign: 'right!important',
                fontSize: '12px!important',
              },
            }}
            disabled={containers.length === 0 || isRoutesLoading || isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ margin: '0' }}>
                  <Typography
                    sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                    component={'span'}
                    variant={'h5'}
                    className={'cargo-inputs-block__text-field'}
                  >
                    {t('Common:Volume')}
                    <Tooltip
                      title={<CargoTypeInformation containers={containers} />}
                      enterTouchDelay={0}
                      leaveTouchDelay={10000}
                      arrow
                    >
                      <span className={'cargo-inputs-block__tooltip'}>?</span>
                    </Tooltip>
                  </Typography>
                </InputAdornment>
              ),
            }}
            onChange={(event) => handleChangeContainerProperty('containerId', event.target.value)}
          >
            {formattedContainers.map((option) => (
              <MenuItem value={option.id} key={option.id}>
                {handleGenerateLocalizedName(option.default_name, option.localized_names_data)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          error={isQuantityInputInvalid}
          focused={isQuantityInputInvalid}
          fullWidth
          variant={'standard'}
          value={getFormattedNumberForInput(ContainerParameters.containerQuantity)}
          type={'number'}
          disabled={isRoutesLoading || isLoading}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                  component={'span'}
                  variant={'h5'}
                >
                  {t('Common:QuantityPcs')}
                </Typography>
              </InputAdornment>
            ),
          }}
          onFocus={() =>
            handleChangeContainerProperty('containerQuantity', getFormattedIntNumber(''))
          }
          onChange={(event) => {
            handleChangeContainerProperty(
              'containerQuantity',
              getFormattedIntNumber(event.target.value)
            );
          }}
        />

        <TextField
          error={isWeightInputInvalid}
          focused={isWeightInputInvalid}
          fullWidth
          variant={'standard'}
          value={getFormattedNumberForInput(ContainerParameters.eachContainerWeight)}
          type={'number'}
          disabled={isRoutesLoading || isLoading}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                  component={'span'}
                  variant={'h5'}
                >
                  {t('OrderList:EachContainerWeight')}
                </Typography>
              </InputAdornment>
            ),
          }}
          onFocus={() =>
            handleChangeContainerProperty('eachContainerWeight', getFormattedIntNumber(''))
          }
          onChange={(event) => {
            handleChangeContainerProperty(
              'eachContainerWeight',
              getFormattedIntNumber(event.target.value)
            );
          }}
        />
        <CrossDockingPalletsParameters
          cargoType="conainer"
          withPallets={ContainerParameters.withPallets}
          palletsLoaded={ContainerParameters.palletsLoaded}
          isCrossDockingEnabled={routeParams.isWithCrossDocking}
          isActionsDisabled={isRoutesLoading || isLoading}
          availablePalletUnits={palletUnits}
          Position={Position}
          onChangeValueByProperty={handleChangeContainerProperty}
          onGenerateLocalizedName={handleGenerateLocalizedName}
        />
      </Box>
      {isDeletable && (
        <IconButton
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-15px',
          }}
          disabled={isRoutesLoading || isLoading}
          size={'small'}
          onClick={() => onDelete()}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default ContainerParametersItemComponent;
